import { useState, useCallback, useRef } from "react";
import { useAuth, useToast } from "./";
import { NOTIFICATION_TYPES } from "../utils";

// Helper to parse money values in API format: {amount: 100, formatted: "$100", currency: "EUR"}
const parseMoneyValue = (moneyField) => {
  if (!moneyField) {
    return { value: 0, currency: null };
  }

  return {
    value: parseFloat(moneyField.amount) || 0,
    currency: moneyField.currency || null,
  };
};

// Helper function to get quarter start and end dates
const getQuarterDates = (quarterOffset = 0) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-11

  // Determine current quarter (0, 1, 2, 3)
  const currentQuarter = Math.floor(currentMonth / 3);

  // Calculate target quarter and year
  const targetQuarter = currentQuarter + quarterOffset;
  const targetYear = currentYear + Math.floor(targetQuarter / 4);
  const normalizedQuarter = ((targetQuarter % 4) + 4) % 4; // Handle negative quarters

  // Quarter start months: Q1=0(Jan), Q2=3(Apr), Q3=6(Jul), Q4=9(Oct)
  const quarterStartMonth = normalizedQuarter * 3;

  // Start of quarter
  const quarterStart = new Date(targetYear, quarterStartMonth, 1);

  // End of quarter (last day of the third month)
  const quarterEnd = new Date(targetYear, quarterStartMonth + 3, 0); // Day 0 = last day of previous month

  return { quarterStart, quarterEnd };
};

// Helper function to calculate days between two dates
const daysBetween = (date1, date2) => {
  const diffTime = Math.abs(date2 - date1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Generate time ranges with proper quarter calculations
const generateTimeRanges = () => {
  const today = new Date();

  // Current quarter
  const { quarterEnd: currentQuarterEnd } = getQuarterDates(0);
  const daysToCurrentQuarterEnd = daysBetween(today, currentQuarterEnd);

  // Next quarter
  const { quarterEnd: nextQuarterEnd } = getQuarterDates(1);
  const daysToNextQuarterEnd = daysBetween(today, nextQuarterEnd);

  // Next 2 quarters (end of quarter after next)
  const { quarterEnd: next2QuartersEnd } = getQuarterDates(2);
  const daysToNext2QuartersEnd = daysBetween(today, next2QuartersEnd);

  // Previous quarters for closed opportunities
  const { quarterStart: lastQuarterStart } = getQuarterDates(-1);
  const daysFromLastQuarterStart = daysBetween(lastQuarterStart, today);

  const { quarterStart: last2QuartersStart } = getQuarterDates(-2);
  const daysFromLast2QuartersStart = daysBetween(last2QuartersStart, today);

  return {
    "Closed this quarter": {
      closed_days: daysToCurrentQuarterEnd + 30,
      open_days: daysToCurrentQuarterEnd,
    }, // Look back a bit more for closed
    "Closed last quarter": {
      closed_days: daysFromLastQuarterStart,
      open_days: daysToCurrentQuarterEnd,
    },
    "Closed last 2 quarters": {
      closed_days: daysFromLast2QuartersStart,
      open_days: daysToCurrentQuarterEnd,
    },
    "Due this quarter": { closed_days: 90, open_days: daysToCurrentQuarterEnd },
    "Due next quarter": { closed_days: 90, open_days: daysToNextQuarterEnd },
    "Due next 2 quarters": {
      closed_days: 90,
      open_days: daysToNext2QuartersEnd,
    },
    "Due in next 12 months": { closed_days: 90, open_days: 365 },
  };
};

// Generate time ranges on module load
const TIME_RANGES = generateTimeRanges();

export const useRenewalForecast = () => {
  const { apiClient } = useAuth();
  const showToast = useToast();
  const [renewalForecastData, setRenewalForecastData] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentTimeRange, setCurrentTimeRange] = useState(
    "Closed this quarter",
  );

  // Use ref to avoid stale closure issues
  const renewalForecastDataRef = useRef({});

  // Keep ref in sync with state
  renewalForecastDataRef.current = renewalForecastData;

  const fetchRenewalForecastSummary = useCallback(
    async (timeRangeName = "Closed this quarter") => {
      const timeRange = TIME_RANGES[timeRangeName];
      if (!timeRange) {
        console.error(
          `Invalid time range: ${timeRangeName}. Available ranges:`,
          Object.keys(TIME_RANGES),
        );
        return null;
      }

      // Check if we already have data for this time range using ref
      const currentData = renewalForecastDataRef.current[timeRangeName];
      if (currentData) {
        return currentData;
      }

      setLoading(true);

      try {
        const url = `/metrics/renewal-forecast-summary/?closed_days=${timeRange.closed_days}&open_days=${timeRange.open_days}`;
        const response = await apiClient.get(url);

        // Store data for this specific time range
        setRenewalForecastData((prev) => ({
          ...prev,
          [timeRangeName]: response.data,
        }));

        setCurrentTimeRange(timeRangeName);
        return response.data;
      } catch (error) {
        console.error("Error fetching renewal forecast data:", error);
        showToast({
          content: "Failed to fetch renewal forecast data",
          type: NOTIFICATION_TYPES.error,
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [apiClient, showToast],
  );

  const fetchMultipleTimeRanges = useCallback(
    async (timeRangeNames = ["Closed this quarter"]) => {
      setLoading(true);
      try {
        const promises = timeRangeNames.map(async (timeRangeName) => {
          // Check if we already have data for this time range using ref
          const currentData = renewalForecastDataRef.current[timeRangeName];
          if (currentData) {
            return { timeRangeName, data: currentData };
          }

          const timeRange = TIME_RANGES[timeRangeName];
          if (!timeRange) {
            console.error(`Invalid time range: ${timeRangeName}`);
            return { timeRangeName, data: null };
          }

          try {
            const response = await apiClient.get(
              `/metrics/renewal-forecast-summary/?closed_days=${timeRange.closed_days}&open_days=${timeRange.open_days}`,
            );
            return { timeRangeName, data: response.data };
          } catch (error) {
            console.error(`Error fetching data for ${timeRangeName}:`, error);
            return { timeRangeName, data: null };
          }
        });

        const results = await Promise.all(promises);

        // Update state with all results
        const newData = {};
        results.forEach(({ timeRangeName, data }) => {
          if (data) {
            newData[timeRangeName] = data;
          }
        });

        if (Object.keys(newData).length > 0) {
          setRenewalForecastData((prev) => ({
            ...prev,
            ...newData,
          }));
        }

        return results;
      } catch (error) {
        console.error("Error fetching multiple time ranges:", error);
        showToast({
          content: "Failed to fetch forecast data",
          type: NOTIFICATION_TYPES.error,
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [apiClient, showToast],
  );

  // Transform data for renewal forecast summary view (per-type aggregated)
  const transformDataForRenewalForecast = useCallback((data) => {
    if (!data) return { openOpportunities: [], closedOpportunities: [] };

    const openOpportunities = [];
    const closedOpportunities = [];

    // Transform open opportunities per type
    if (data.open_opportunities_per_type?.summary) {
      Object.entries(data.open_opportunities_per_type.summary).forEach(
        ([oppType, values]) => {
          // Skip the "Total" row for now, we'll handle it separately
          if (oppType !== "Total" && oppType !== "Total (all open opps)") {
            const deltaMrr = parseMoneyValue(values.delta_mrr);
            const deltaArr = parseMoneyValue(values.delta_arr);
            openOpportunities.push({
              oppType,
              accounts: values.accounts || 0,
              contracts: values.contracts || 0,
              deltaMrr: deltaMrr.value,
              deltaArr: deltaArr.value,
              currency: deltaMrr.currency || deltaArr.currency,
            });
          }
        },
      );
    }

    // Transform closed opportunities per type
    if (data.closed_opportunities_per_type?.summary) {
      Object.entries(data.closed_opportunities_per_type.summary).forEach(
        ([oppType, values]) => {
          // Skip the "Total" row for now, we'll handle it separately
          if (oppType !== "Total" && oppType !== "Total (all closed opps)") {
            const deltaMrr = parseMoneyValue(values.delta_mrr);
            const deltaArr = parseMoneyValue(values.delta_arr);
            closedOpportunities.push({
              oppType,
              accounts: values.accounts || 0,
              contracts: values.contracts || 0,
              deltaMrr: deltaMrr.value,
              deltaArr: deltaArr.value,
              currency: deltaMrr.currency || deltaArr.currency,
            });
          }
        },
      );
    }

    return { openOpportunities, closedOpportunities };
  }, []);

  // Transform data for detailed per-account views (pipeline evolution, renewed accounts)
  const transformDataForDetailedView = useCallback((data, isOpen = true) => {
    if (!data) return [];

    const accountData = isOpen
      ? data.open_opportunities_per_account
      : data.closed_opportunities_per_account;

    if (!accountData?.deals) return [];

    return accountData.deals.map((deal) => {
      const deltaMrr = parseMoneyValue(deal.delta_mrr);
      const deltaArr = parseMoneyValue(deal.delta_arr);
      return {
        contractName: deal.contract_name || "",
        accountName: deal.account_name || "",
        accountId: deal.account_id || "",
        logo: deal.account_logo || "",
        csmName: deal.csm_name || "",
        oppType: deal.opp_type || "",
        oppName: deal.opp_name || "",
        stage: deal.stage || "",
        closeDate: deal.close_date || "",
        deltaMrr: deltaMrr.value,
        deltaArr: deltaArr.value,
        currency: deltaMrr.currency || deltaArr.currency,
        pctChange: deal.pct_change || "0%",
        logoLost: deal.logo_lost || "No",
      };
    });
  }, []);

  // Get totals for detailed views
  const getTotalsForDetailedView = useCallback((data, isOpen = true) => {
    if (!data) return { deltaMrr: 0, deltaArr: 0, currency: null };

    const accountData = isOpen
      ? data.open_opportunities_per_account
      : data.closed_opportunities_per_account;

    if (!accountData?.totals)
      return { deltaMrr: 0, deltaArr: 0, currency: null };

    const deltaMrr = parseMoneyValue(accountData.totals.delta_mrr);
    const deltaArr = parseMoneyValue(accountData.totals.delta_arr);

    return {
      deltaMrr: deltaMrr.value,
      deltaArr: deltaArr.value,
      currency: deltaMrr.currency || deltaArr.currency,
    };
  }, []);

  // Get grand totals from per-type data
  const getGrandTotalsFromPerType = useCallback((data, isOpen = true) => {
    if (!data)
      return {
        accounts: 0,
        contracts: 0,
        deltaMrr: 0,
        deltaArr: 0,
        currency: null,
      };

    const typeData = isOpen
      ? data.open_opportunities_per_type
      : data.closed_opportunities_per_type;

    if (!typeData?.summary)
      return {
        accounts: 0,
        contracts: 0,
        deltaMrr: 0,
        deltaArr: 0,
        currency: null,
      };

    // Look for the total row - it could be "Total" or "Total (all open opps)" etc.
    const totalKey = Object.keys(typeData.summary).find((key) =>
      key.toLowerCase().includes("total"),
    );

    if (!totalKey || !typeData.summary[totalKey]) {
      return {
        accounts: 0,
        contracts: 0,
        deltaMrr: 0,
        deltaArr: 0,
        currency: null,
      };
    }

    const totals = typeData.summary[totalKey];
    const deltaMrr = parseMoneyValue(totals.delta_mrr);
    const deltaArr = parseMoneyValue(totals.delta_arr);

    return {
      accounts: totals.accounts === "—" ? 0 : totals.accounts || 0,
      contracts: totals.contracts || 0,
      deltaMrr: deltaMrr.value,
      deltaArr: deltaArr.value,
      currency: deltaMrr.currency || deltaArr.currency,
    };
  }, []);

  const getCurrentTimeRangeData = useCallback(() => {
    return renewalForecastDataRef.current[currentTimeRange] || null;
  }, [currentTimeRange]);

  // Get data for a specific time range
  const getTimeRangeData = useCallback((timeRangeName) => {
    return renewalForecastDataRef.current[timeRangeName] || null;
  }, []);

  // Clear cached data (useful for refreshing)
  const clearCache = useCallback(() => {
    setRenewalForecastData({});
    renewalForecastDataRef.current = {};
  }, []);

  // Get human readable info about time ranges (for debugging)
  const getTimeRangeInfo = useCallback(() => {
    const info = {};

    Object.entries(TIME_RANGES).forEach(([name, params]) => {
      info[name] = {
        ...params,
        description: `Closed: ${params.closed_days} days, Open: ${params.open_days} days`,
      };
    });

    return info;
  }, []);

  return {
    renewalForecastData,
    loading,
    currentTimeRange,
    fetchRenewalForecastSummary,
    fetchMultipleTimeRanges,
    transformDataForRenewalForecast,
    transformDataForDetailedView,
    getTotalsForDetailedView,
    getGrandTotalsFromPerType,
    getCurrentTimeRangeData,
    getTimeRangeData,
    clearCache,
    timeRanges: TIME_RANGES,
    availableTimeRanges: Object.keys(TIME_RANGES),
    getTimeRangeInfo, // For debugging
  };
};
